import ExternalLink from '@components/external-link/ExternalLink';
import { MapGeometry } from '@src/api/types';
import { ErrorMessages } from '@src/utils/enums';
import * as yup from 'yup';

type SpeciesDataItem = {
  requestID: number;
  version: number;
  permitID: number;
  speciesID: number;
  ipacSpeciesID: number;
  commonName: string;
  scientificName: string;
  listingStatus: string;
  profileURL: string;
  isAffectedSpecies: boolean;
  createdDate: string;
  modifiedDate: string | null;
  createdBy: number;
  modifiedBy: number | null;
};

export const formatSpeciesList = (species: SpeciesDataItem[]) =>
  species.map((speciesData) => ({
    optionalCommonName: speciesData?.commonName,
    listingStatusName: speciesData?.listingStatus,
    speciesProfileUrl: speciesData?.profileURL,
    optionalScientificName: speciesData?.scientificName,
  }));

export const gpSchema = {
  affectedSpecies: yup.string().required(ErrorMessages.Required),
  impactedHistoricalProperty: yup.string().required(ErrorMessages.Required),
  isWildScenicRiver: yup.string().required(ErrorMessages.SelectOption),
  wildScenicRiverName: yup.string().when('isWildScenicRiver', {
    is: 'Yes',
    then: (schema) => schema.required(ErrorMessages.Required),
  }),
  isS408PermissionRequired: yup.string().required(ErrorMessages.SelectOption),
  isS408RequestSubmitted: yup
    .string()
    .nullable()
    .when('isS408PermissionRequired', {
      is: 'Yes',
      then: (schema) => schema.required(ErrorMessages.SelectOption),
    }),
  s408RequestSubmittedDate: yup
    .string()
    .nullable()
    .when('isS408RequestSubmitted', {
      is: 'true',
      then: (schema) => schema.required(ErrorMessages.Required),
    }),
};

export const ipSchema = {
  affectedSpecies: yup.string().required(ErrorMessages.Required),
  impactedHistoricalProperty: yup.string().required(ErrorMessages.Required),
  isWildScenicRiver: yup.string().required(ErrorMessages.SelectOption),
  wildScenicRiverName: yup.string().when('isWildScenicRiver', {
    is: 'Yes',
    then: (schema) => schema.required(ErrorMessages.Required),
  }),
  isS408PermissionRequired: yup.string().required(ErrorMessages.SelectOption),
  isS408RequestSubmitted: yup
    .string()
    .nullable()
    .when('isS408PermissionRequired', {
      is: 'Yes',
      then: (schema) => schema.required(ErrorMessages.SelectOption),
    }),
  s408RequestSubmittedDate: yup
    .string()
    .nullable()
    .when('isS408RequestSubmitted', {
      is: 'true',
      then: (schema) => schema.required(ErrorMessages.Required),
    }),
};

export const otherLawsAndRegulationsText = {
  // IP request text
  false: {
    affectedSpecies: {
      label: (
        <>
          Describe your current knowledge of endangered species that may be affected by activities occurring within the
          project or action area. If no endangered species exist, enter "None" or "I'm not sure" if you can't determine
          this information.
        </>
      ),
      tooltipText: (
        <>
          It is the USACE responsibility to define action area for the project. While not required, it can be helpful to
          work with the USACE to determine the action area for the project during the pre-application phase of the
          project. If you have knowledge of endangered species that may be affected by activities occurring within the
          action area, you should list that information here.
        </>
      ),
    },
    endangeredSpeciesAlert: {
      label: (
        <>
          List the name(s) of any species listed as endangered or threatened under the Endangered Species Act that might
          be affected by the proposed activity or utilize the designated critical habitat that might be affected by the
          proposed activity.
        </>
      ),
      tooltipText: (
        <>
          It is the USACE responsibility to define action area for the project. While not required, it can be helpful to
          work with the USACE to determine the action area for the project during the pre-application phase of the
          project. If you have knowledge of endangered species that may be affected by activities occurring within the
          action area, you should list that information here.
        </>
      ),
    },
    impactedHistoricalProperty: {
      label: (
        <>
          Describe your current knowledge of historic properties that would have the potential to be impacted by
          activities occurring within the area of potential effect/permit area. If no historic properties exist, enter
          "None" or "I'm not sure" if you can't determine this information. +
        </>
      ),
      tooltipText: (
        <>
          It is the USACE responsibility to define area of potential effect/permit area for the project. While not
          required, it can be helpful to work with the USACE to determine the area of potential effect/permit area for
          the project during the pre-application phase of the project. If you have knowledge of historic properties that
          would have the potential to be impacted by activities occurring within the area of potential effect/permit
          area, you should list that information here.
        </>
      ),
    },
    isS408PermissionRequired: {
      label: (
        <>
          Will the proposed activity require permission from the USACE pursuant to 33 U.S.C. 408 because it will alter
          or temporarily or permanently occupy or use a U.S. Army Corps of Engineers federally authorized civil works
          project?
        </>
      ),
      tooltipText: '',
    },
    isWildScenicRiver: {
      label: (
        <>
          Will the proposed activity occur in a component of the National Wild and Scenic River System or in a river
          officially designated by Congress as a "study river" for possible inclusion in the system while the river is
          in an official study status?
        </>
      ),
      tooltipText: '',
    },
    wildScenicRiverName: {
      tooltipText: (
        <>
          If the proposed activity will occur in a river in the National Wild and Scenic River System or in a river
          officially designated by Congress as a "study river" under the Wild and Scenic Rivers Act, provide the name of
          the river. For a list of Wild and Scenic Rivers and study rivers, please visit{' '}
          <span className='text-bold'>
            <ExternalLink href='http://www.rivers.gov' title='Rivers.gov' content='http://www.rivers.gov/' />
          </span>
        </>
      ),
    },
  },

  // GP request text
  true: {
    affectedSpecies: {
      label: (
        <>
          List any Additional Threatened or Endangered Species not included above. If no endangered species exist, enter
          "None" or "I'm not sure" if you can't determine this information.
        </>
      ),
      tooltipText: (
        <>
          If you are not a federal agency, and if any listed species or designated critical habitat might be affected or
          is in the vicinity of the proposed Nationwide Permit activity, or if the proposed nationwide permit activity
          is located in designated critical habitat, list the name(s) of those endangered or threatened species that
          might be affected by the proposed Nationwide Permit activity or utilize the designated critical habitat that
          might be affected by the proposed Nationwide Permit activity. If you are a Federal agency, and the proposed
          Nationwide Permit activity requires a preconstruction notification, you must provide documentation
          demonstrating compliance with Section 7 of the Endangered Species Act.
        </>
      ),
    },
    endangeredSpeciesAlert: {
      label: (
        <>
          List the name(s) of any species listed as endangered or threatened under the Endangered Species Act that might
          be affected by the proposed Nationwide Permit activity or utilize the designated critical habitat that might
          be affected by the proposed Nationwide Permit activity.
        </>
      ),
      tooltipText: (
        <>
          If you are not a federal agency, and if any listed species or designated critical habitat might be affected or
          is in the vicinity of the proposed Nationwide Permit activity, or if the proposed nationwide permit activity
          is located in designated critical habitat, list the name(s) of those endangered or threatened species that
          might be affected by the proposed Nationwide Permit activity or utilize the designated critical habitat that
          might be affected by the proposed Nationwide Permit activity. If you are a Federal agency, and the proposed
          Nationwide Permit activity requires a preconstruction notification, you must provide documentation
          demonstrating compliance with Section 7 of the Endangered Species Act.
        </>
      ),
    },
    impactedHistoricalProperty: {
      label: (
        <>
          List any historic properties that have the potential to be affected by the proposed Nationwide Permit
          activity. If no historic properties exist, enter "None" or "I'm not sure" if you can't determine this
          information.
        </>
      ),
      tooltipText: (
        <>
          If you are not a federal agency, and if any historic properties have the potential to be affected by the
          proposed Nationwide Permit activity, list the name(s) of those historic properties that have the potential to
          be affected by the proposed Nationwide Permit activity. If you are a Federal agency, and the proposed
          nationwide permit activity requires a PCN, you must provide documentation demonstrating compliance with
          Section 106 of the National Historic Preservation Act.
        </>
      ),
    },
    isS408PermissionRequired: {
      label: (
        <>
          Will the proposed Nationwide Permit activity require permission from the USACE pursuant to 33 U.S.C. 408
          because it will alter or temporarily or permanently occupy or use a U.S. Army Corps of Engineers federally
          authorized civil works project?
        </>
      ),
      tooltipText: '',
    },
    isWildScenicRiver: {
      label: (
        <>
          Will the proposed Nationwide Permit activity occur in a component of the National Wild and Scenic River System
          or in a river officially designated by Congress as a "study river" for possible inclusion in the system while
          the river is in an official study status?
        </>
      ),
      tooltipText: '',
    },
    wildScenicRiverName: {
      tooltipText: (
        <>
          If the proposed Nationwide Permit activity will occur in a river in the National Wild and Scenic River System
          or in a river officially designated by Congress as a "study river" under the Wild and Scenic Rivers Act,
          provide the name of the river. For a list of Wild and Scenic Rivers and study rivers, please visit{' '}
          <span className='text-bold'>
            <ExternalLink href='http://www.rivers.gov' title='Rivers.gov' content='http://www.rivers.gov/' />
          </span>
        </>
      ),
    },
  },

  // Common
  section408: {
    tooltipText: (
      <>
        <p>
          The U.S. Army Corps of Engineers (USACE) Section 408 program provides permission for people, towns, or
          businesses to alter or impact a USACE project, like a dam, levee, or waterway.
        </p>
        <p>
          Before any changes can be made, the Section 408 program reviews alterations to ensure they won't harm the
          project or the public.
        </p>
        <p>
          You can find more information about the Section 408 at{' '}
          <ExternalLink href='https://www.usace.army.mil/Missions/Civil-Works/Section408/' />
        </p>
      </>
    ),
  },
};

// IPAC projectLocationWKT - accepts only LineString, Polygon, or MultiPolygon
// https://ipac.ecosphere.fws.gov/location/api#tag/resources/operation/locationResources
export const getProjectLocationWkt = (projectGeometry: MapGeometry) => {
  let projectLocationWkt = '';

  switch (projectGeometry?.type) {
    case 'Point':
      // Converting Point Geometry to LineString - requires two coordinates so repeat the point coordinate once
      projectLocationWkt = `LINESTRING (${projectGeometry?.coordinates?.[0]} ${projectGeometry?.coordinates?.[1]},${projectGeometry?.coordinates?.[0]} ${projectGeometry?.coordinates?.[1]})`;
      break;
    case 'LineString':
    case 'MultiPoint':
      // Converting MultiPoint Geometry to LineString
      const formattedCoords = projectGeometry?.coordinates.map((coord) => coord.join(' ')).join(', ');

      projectLocationWkt = `LINESTRING (${formattedCoords})`;
      break;
    case 'MultiLineString':
      // Converting MultiLineString to LineString
      projectLocationWkt = 'LINESTRING (';

      projectGeometry?.coordinates?.forEach((lineString) => {
        const formattedCoords = lineString?.map((coord) => coord?.join(' '))?.join(', ');
        projectLocationWkt += formattedCoords + ', ';
      });

      projectLocationWkt = projectLocationWkt.substring(0, projectLocationWkt.lastIndexOf(', '));
      projectLocationWkt += ')';

      break;
    default:
      break;
  }

  return projectLocationWkt;
};

// IPAC location.footprint - accepts only Polygon or MultiPolygon
// https://ipac.ecosphere.fws.gov/location/api#tag/resources/operation/locationResources
export const getSpeciesFootprint = (projectGeometry: MapGeometry) => {
  let projectGeometryIPAC;

  switch (projectGeometry?.type) {
    case 'Polygon':
      projectGeometryIPAC = {
        coordinates: projectGeometry?.coordinates,
        type: 'Polygon',
      };
      break;
    case 'MultiPolygon':
      projectGeometryIPAC = {
        coordinates: projectGeometry?.coordinates,
        type: 'MultiPolygon',
      };
      break;
    default:
      break;
  }

  const updatedFootprint = {
    coordinates: projectGeometryIPAC?.coordinates,
    type: projectGeometryIPAC?.type,
  };

  return updatedFootprint;
};
